import cx from 'classnames';
import {map, take} from 'lodash';
import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, CreditCardIcon, InformationModal, Table, TableBody, TableCell, TableRow} from 'components';
import {UserContext} from 'context';
import {TransactionStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformTransaction, CreditCard} from 'types';
import {dateUtils} from 'utils';
import {formatDate, transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.TRANSACTIONS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

const INITIAL_ITEM_COUNT = 10;

type Props = {
    transactions: BillingPlatformTransaction[];
}

export const TransactionsTable: React.FC<Props> = ({transactions}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);

    const hasViewAllBtn = useMemo(() => transactions?.length > INITIAL_ITEM_COUNT, [transactions]);
    const initialTransactions = useMemo(() => take(transactions, INITIAL_ITEM_COUNT), [transactions]);
    const [modalOpen, setModalOpen] = useState(false);

    const headers = useMemo(() => [
        t(T_KEYS.COLUMNS.DATE),
        t(T_KEYS.COLUMNS.TYPE),
        t(T_KEYS.COLUMNS.PAYMENT_SOURCE),
        t(T_KEYS.COLUMNS.STATUS),
        t(T_KEYS.COLUMNS.AMOUNT),
    ], [t]);

    const formatPaymentDate = useCallback((date: Date) => {
        const dateTimeFormat = dateUtils.getDateTimeFormat(dateFormat, timeFormat);
        return formatDate(date, timeZone, dateTimeFormat);
    }, [timeZone, dateFormat, timeFormat]);

    const renderCreditCard = useCallback((creditCard: Partial<CreditCard>, paymentSourceId: string) => creditCard ? (
        <div className='tw-flex tw-items-center'>
            {creditCard.brand && <CreditCardIcon className='tw-mr-2' iconSize='lg' name={creditCard.brand} />}
            <div>{creditCard.last4 || paymentSourceId}</div>
        </div>
    ) : null, []);

    const renderTransactionTable = useCallback((items: BillingPlatformTransaction[]) => (
        <Table noData={!items?.length} headerNames={headers}>
            <TableBody>
                {map(items, (transaction, index) => (
                    <TableRow key={index}>
                        <TableCell>{formatPaymentDate(transaction.date)}</TableCell>
                        <TableCell>{transaction.type}</TableCell>
                        <TableCell>{renderCreditCard(transaction.paymentDetails?.card, transaction.paymentSourceId)}</TableCell>
                        <TableCell className={cx({
                            'tw-text-success': transaction.status === TransactionStatus.Success,
                            'tw-text-alert': transaction.status === TransactionStatus.Failure
                        })}
                        >
                            {transaction.status}
                        </TableCell>
                        <TableCell className='tw-font-bold'>
                            {transformUtils.formatPrice(transaction.amount, transaction.currency)}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    ), [formatPaymentDate, headers, renderCreditCard]);

    return (
        <div className='-sm:tw-hidden'>
            {renderTransactionTable(initialTransactions)}
            {hasViewAllBtn &&
                <div className='tw-text-right'>
                    <Button className='tw-mt-4' onClick={() => setModalOpen(true)}>{t(T_KEYS_COMMON.BUTTONS.VIEW_ALL)}</Button>
                </div>}
            <InformationModal
                title={t(T_KEYS.VIEW_ALL_MODAL.TITLE)}
                open={modalOpen}
                maxWidth='md'
                fullWidth
                onClose={() => setModalOpen(false)}
            >
                {renderTransactionTable(transactions)}
            </InformationModal>
        </div>
    );
};